// Agent details / config inspection view. fn-52.3 / R3.
//
// **Acceptance**: a read-only details view, reachable from /agents and
// linked from the run-detail header, showing the deployed agent's:
// system prompt, model id, tools + per-tool permission policy, skills,
// MCP servers, deploy status (full ConfigRepoAgentStatus set), ma_agent_id
// + version, ma_environment_id, and repo_path.
//
// **Sources** (reads only — no agent mutation here; that stays the
// config-repo deploy path, ADR 0016):
//   - `agents.byId` → the agent record + `current_version` (now surfaced
//     additively, fn-52.3). System prompt = `current_version.prompt_snapshot`;
//     tool posture = `agent.config.tools`; `ma_environment_id` =
//     `agent.config.ma_environment_id` (freeform config JSON, NOT a typed
//     field — see config-repo-manager.ts:199, no contract change needed).
//   - `configRepoAgents.list` → the config-repo projection (deploy status,
//     skills[], mcp_servers[], ma_agent_id, repo_path), matched by
//     `agent_id === agent.id` (ConfigRepoAgent carries agent_id), with name
//     as a documented fallback (names can drift/collide). Skills/MCP have
//     no `byId` endpoint, so we read the existing list payload.
//
// **R6**: all reads go through the existing `requireScope`-gated CP
// endpoints; agent config is global-read by design (fn-8). No
// session/account-scoped data is surfaced here.

import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { trpc } from '../../trpc-client.js';
import { formatTime } from '../format.js';
import { AgentToolsPanel } from './agent-tools-panel.js';
import {
  type ConfigRepoAgentRow,
  formatConfigRepoAgentMcpStatus,
  formatConfigRepoAgentSkillStatus,
  formatConfigRepoAgentStatus,
} from './list.js';
import type { AgentMemoryStatus } from './merge.js';
import { useIsPlatformAdmin } from './use-is-platform-admin.js';

// We pull the whole config-repo list (CP-capped at 200) and match a row
// client-side. Agents are global + few (fn-8 / AIP-28), so one page
// covers the full set; there is no config-repo `byId` endpoint.
const CONFIG_REPO_LIST_LIMIT = 200;

/**
 * Read `ma_environment_id` from the agent's freeform `config` JSON. It is
 * NOT a typed field on `AgentSchema` — the deploy stamps it into `config`
 * (`config-repo-manager.ts:199`). Returns null when absent or not a
 * string. Pure + exported for unit tests. fn-52.3 / R3.
 */
export function readMaEnvironmentId(config: Record<string, unknown> | null): string | null {
  if (config === null || typeof config !== 'object') return null;
  const value = (config as { ma_environment_id?: unknown }).ma_environment_id;
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * Read a top-level string field from the agent's freeform `config` JSON
 * (e.g. `repo_path`, `model`). Returns null when absent / not a string.
 * Pure + exported for unit tests.
 */
export function readConfigString(
  config: Record<string, unknown> | null,
  key: string,
): string | null {
  if (config === null || typeof config !== 'object') return null;
  const value = (config as Record<string, unknown>)[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * Match the config-repo projection row for a deployed agent. Primary key
 * is `agent_id === agentId` (ConfigRepoAgent carries the platform agent
 * id once deployed); falls back to a case-sensitive `name` match only
 * when no id match exists, because repo names can drift/collide. Returns
 * null when neither matches. Pure + exported for unit tests. fn-52.3 / R3.
 */
export function matchConfigRepoAgent(
  rows: readonly ConfigRepoAgentRow[],
  agentId: string,
  agentName: string,
): ConfigRepoAgentRow | null {
  const byId = rows.find((r) => r.agent_id !== null && r.agent_id === agentId);
  if (byId !== undefined) return byId;
  const byName = rows.find((r) => r.name === agentName);
  return byName ?? null;
}

/**
 * fn-75 / ADR 0035 / R7 — which archive lifecycle action the detail-page
 * admin block should offer for a given agent. Pure + exported for unit
 * tests (the console convention: page logic is tested through exported
 * pure functions, not the mounted tRPC tree).
 *
 *   - `'none'`      — config-repo agents (owned by the repo sync, archived
 *                     via the repo only) get NO archive action.
 *   - `'archive'`   — an active `source='console'` agent can be archived.
 *   - `'unarchive'` — an archived `source='console'` agent can be reverted.
 *
 * The admin gate (`admin.isAdmin`) is applied by the caller — this helper
 * decides only the source/state axis, mirroring how the Edit affordance is
 * `admin.isAdmin && source === 'console'`.
 */
export type AgentArchiveAction = 'none' | 'archive' | 'unarchive';

export function agentArchiveAction(agent: {
  readonly source: 'config_repo' | 'console';
  readonly archived: boolean;
}): AgentArchiveAction {
  if (agent.source !== 'console') return 'none';
  return agent.archived ? 'unarchive' : 'archive';
}

export function AgentDetailPage(): React.ReactElement {
  const params = useParams<{ id: string }>();
  const agentId = params.id ?? null;
  if (agentId === null || agentId.length === 0) {
    return (
      <section className="page-agent-detail" role="alert">
        <h1>Agent</h1>
        <p>Missing agent id.</p>
      </section>
    );
  }
  return <AgentDetailInner agentId={agentId} />;
}

function AgentDetailInner({ agentId }: { agentId: string }): React.ReactElement {
  const admin = useIsPlatformAdmin();
  const agentQuery = trpc.agents.byId.useQuery({ agentId }, { enabled: agentId.length > 0 });
  // Config-repo projection (deploy status, skills, mcp_servers, repo_path).
  // Independent of the agent record; a deployed agent has a row, a
  // never-scanned one may not.
  const configRepoQuery = trpc.configRepoAgents.list.useQuery({
    limit: CONFIG_REPO_LIST_LIMIT,
    sort_by: 'name',
    sort_dir: 'asc',
  });

  if (agentQuery.isPending) {
    return (
      <section className="page-agent-detail" aria-busy="true">
        <h1>Agent</h1>
        <p>Loading agent…</p>
      </section>
    );
  }
  if (agentQuery.error !== null) {
    return (
      <section className="page-agent-detail" role="alert">
        <header className="agent-detail-header">
          <Link to="/agents" className="agent-detail-back">
            ← Agents
          </Link>
        </header>
        <h1>Agent</h1>
        <p>Failed to load agent: {agentQuery.error.message}</p>
      </section>
    );
  }

  const agent = agentQuery.data;
  const config = agent.config;
  const version = agent.current_version;
  const systemPrompt = version.prompt_snapshot;
  // R3: inspect the IMMUTABLE deployed version snapshot for model / tools /
  // raw config — not the mutable agent row. Falls back to the live config only
  // when no snapshot exists. `ma_environment_id` stays sourced from the live
  // config (stamped there at deploy, per spec), not the snapshot.
  const versionConfig = version.config_snapshot ?? config;
  const maEnvironmentId = readMaEnvironmentId(config);
  // Config-repo projection state. We distinguish three outcomes so a
  // FAILED read (403/500/network) never silently degrades into a false
  // "No deploy status" + missing skills/MCP — that hides a required half
  // of the details view (R3). Only a SUCCESSFUL read with no matching row
  // is "no deploy status".
  const configRepoFailed = configRepoQuery.error !== null;
  const configRepoLoading = configRepoQuery.isPending;
  const configRepoRows = configRepoQuery.data?.data ?? [];
  const repoAgent = matchConfigRepoAgent(configRepoRows, agent.id, agent.name);
  // Prefer the config-repo projection for the config-managed identifiers;
  // fall back to the agent's own freeform config when the projection row
  // is unavailable (e.g. config repo never scanned for this agent, or the
  // projection read failed).
  const maAgentId = repoAgent?.ma_agent_id ?? readConfigString(config, 'ma_agent_id');
  const maAgentVersion = repoAgent?.ma_agent_version ?? null;
  const repoPath = repoAgent?.repo_path ?? readConfigString(config, 'repo_path');
  // Model: prefer the config-repo projection's model_id, then the versioned
  // config snapshot (model_id or model key). Em-dash when none is present.
  const modelId =
    repoAgent?.model_id ??
    readConfigString(versionConfig, 'model_id') ??
    readConfigString(versionConfig, 'model') ??
    '—';

  return (
    <section className="page-agent-detail">
      <header className="agent-detail-header">
        <Link to="/agents" className="agent-detail-back">
          ← Agents
        </Link>
        {/* fn-71 / ADR 0035 — admin-only header actions. Edit is shown only
            for console-authored agents (a config-repo agent is edited via the
            repo); "Try it" (R12) starts a matter-less test session of any
            agent — useful right after create/edit (this detail page is the
            create/edit landing). */}
        {admin.isAdmin && (
          <div className="agent-detail-actions">
            <Link
              to={`/agents/${encodeURIComponent(agent.id)}/try`}
              className="btn btn--primary agent-detail-try"
            >
              Try it
            </Link>
            {agent.source === 'console' && (
              <Link
                to={`/agents/${encodeURIComponent(agent.id)}/edit`}
                className="btn btn--secondary agent-detail-edit"
              >
                Edit agent
              </Link>
            )}
            {/* fn-75 / ADR 0035 / R7 — soft-archive lifecycle. Shown only for
                `source='console'` agents (config-repo agents are archived via
                the repo); Archive when active, Unarchive when archived. */}
            <AgentArchiveActions
              agentId={agent.id}
              agentName={agent.name}
              action={agentArchiveAction(agent)}
            />
          </div>
        )}
      </header>
      <h1>{agent.name}</h1>
      <div className="sb-chip-row agent-detail-chips">
        {repoAgent !== null ? (
          <span
            className={`sb-chip skills-status-${repoAgent.status}`}
            title="Config-repo deploy status"
          >
            {formatConfigRepoAgentStatus(repoAgent.status)}
          </span>
        ) : configRepoFailed ? (
          // Failed read — do NOT claim "No deploy status" (that's a false
          // negative); the status is unknown until the config-repo read
          // succeeds. The retry lives in the skills/MCP section below.
          <span className="sb-chip" title="Could not load the config-repo deploy status">
            Deploy status unavailable
          </span>
        ) : configRepoLoading ? (
          <span className="sb-chip" title="Loading config-repo deploy status">
            Deploy status…
          </span>
        ) : (
          <span className="sb-chip" title="No config-repo projection found for this agent">
            No deploy status
          </span>
        )}
        <span className="sb-chip" title={agent.id}>
          <code>id {agent.id.slice(0, 8)}…</code>
        </span>
        <span className="sb-chip" title="Model id">
          {modelId}
        </span>
        <span className="sb-chip" title="Current agent version">
          v{version.version_number}
        </span>
      </div>

      {/* Tool posture — same renderer as the run-detail header. */}
      <AgentToolsPanel agent={{ ...agent, config: versionConfig }} />

      {/* Identifiers — the config-managed + Managed-Agent ids. */}
      <dl className="agent-detail-meta">
        <div>
          <dt>MA agent id</dt>
          <dd>{maAgentId !== null && maAgentId.length > 0 ? <code>{maAgentId}</code> : '—'}</dd>
        </div>
        <div>
          <dt>MA agent version</dt>
          <dd>{maAgentVersion !== null ? maAgentVersion : '—'}</dd>
        </div>
        <div>
          <dt>MA environment id</dt>
          <dd>{maEnvironmentId !== null ? <code>{maEnvironmentId}</code> : '—'}</dd>
        </div>
        <div>
          <dt>Repo path</dt>
          <dd>{repoPath !== null && repoPath.length > 0 ? <code>{repoPath}</code> : '—'}</dd>
        </div>
        <div>
          <dt>Published</dt>
          <dd>
            {formatTime(version.published_at)}
            {version.published_by.length > 0 && (
              <span className="agent-detail-meta-by"> · by {version.published_by}</span>
            )}
          </dd>
        </div>
      </dl>

      {/* System prompt — scrollable monospace block + copy button. We do
          NOT dump raw JSON here; the prompt is the operator's primary
          read. A collapsed raw-config escape hatch lives below. */}
      <SystemPromptSection prompt={systemPrompt} />

      {/* Skills + MCP servers — read from the config-repo projection
          (no byId endpoint). Statuses render via the config-repo chip
          classes, NOT StatusPill (which is session-only). A FAILED read
          surfaces a visible, retryable error rather than silently
          omitting this required section (R3). */}
      {repoAgent !== null ? (
        <DependenciesSection agent={repoAgent} />
      ) : configRepoFailed ? (
        <p className="agent-detail-deps-error" role="alert">
          Could not load skills + MCP servers: {configRepoQuery.error.message}{' '}
          <button
            type="button"
            className="agent-detail-deps-retry"
            onClick={() => void configRepoQuery.refetch()}
            disabled={configRepoQuery.isFetching}
          >
            {configRepoQuery.isFetching ? 'Retrying…' : 'Retry'}
          </button>
        </p>
      ) : configRepoLoading ? (
        <p className="agent-detail-deps-loading">Loading skills + MCP servers…</p>
      ) : null}

      {/* Memory (ADR 0043) — the agent's attachment map, read from the byId
          `agent.memory` record (no config-repo projection needed). */}
      <MemorySection memory={agent.memory} />

      {/* Raw config escape hatch — collapsed by default. */}
      <RawConfigSection config={versionConfig} />
    </section>
  );
}

/**
 * fn-75 / ADR 0035 / R7 — the Archive / Unarchive control for the detail
 * header. Owns a two-step confirm (a bare click never mutates) and wires the
 * `agents.archive` / `agents.unarchive` tRPC mutations (BFF-origin — the
 * split-origin-safe vanilla client behind `trpc`, never a bare `fetch`). On
 * success it invalidates the `agents.byId` cache so the page re-reads the new
 * `archived` state (and the list view picks it up on next visit). Renders
 * nothing when there is no action (config-repo agents).
 */
function AgentArchiveActions({
  agentId,
  agentName,
  action,
}: {
  readonly agentId: string;
  readonly agentName: string;
  readonly action: AgentArchiveAction;
}): React.ReactElement | null {
  const utils = trpc.useUtils();
  const [confirming, setConfirming] = useState(false);
  const archiveMutation = trpc.agents.archive.useMutation();
  const unarchiveMutation = trpc.agents.unarchive.useMutation();

  if (action === 'none') return null;

  const mutation = action === 'archive' ? archiveMutation : unarchiveMutation;
  const isPending = archiveMutation.isPending || unarchiveMutation.isPending;
  const label = action === 'archive' ? 'Archive' : 'Unarchive';

  const run = (): void => {
    void (async () => {
      try {
        await mutation.mutateAsync({ agentId });
        // Refresh the detail read (new `archived` state) + any cached list.
        await Promise.all([
          utils.agents.byId.invalidate({ agentId }),
          utils.agents.list.invalidate(),
        ]);
        setConfirming(false);
      } catch {
        // The mutation error state drives the inline message below.
      }
    })();
  };

  return (
    <span className="agent-detail-archive">
      {confirming ? (
        <>
          <span className="agent-detail-archive-prompt">
            {action === 'archive'
              ? `Archive "${agentName}"? It disappears from the agent list and start picker; you can unarchive it later.`
              : `Unarchive "${agentName}"? It reappears in the agent list.`}
          </span>
          <button
            type="button"
            className="btn btn--primary agent-detail-archive-confirm"
            disabled={isPending}
            onClick={run}
          >
            {isPending ? `${label}…` : `Confirm ${label.toLowerCase()}`}
          </button>
          <button
            type="button"
            className="btn btn--ghost agent-detail-archive-cancel"
            disabled={isPending}
            onClick={() => setConfirming(false)}
          >
            Cancel
          </button>
        </>
      ) : (
        <button
          type="button"
          className="btn btn--tertiary agent-detail-archive-button"
          onClick={() => setConfirming(true)}
        >
          {label}
        </button>
      )}
      {mutation.error !== null && (
        <span className="agent-detail-archive-error" role="alert">
          Failed to {label.toLowerCase()}: {mutation.error.message}
        </span>
      )}
    </span>
  );
}

function SystemPromptSection({ prompt }: { prompt: string }): React.ReactElement {
  const [copied, setCopied] = useState(false);
  // Unmount-guard the async clipboard success path AND the delayed
  // setCopied(false) (.flow/memory unmount-guard-async-success-callbacks):
  // navigating away right after clicking Copy must not setState on an
  // unmounted component. `liveRef` gates the late callbacks; the effect
  // clears any pending reset timer on unmount.
  const liveRef = useRef(true);
  const resetTimer = useRef<number | null>(null);
  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
      if (resetTimer.current !== null) window.clearTimeout(resetTimer.current);
    };
  }, []);
  const onCopy = (): void => {
    // Best-effort; clipboard may be unavailable (no-https / permission).
    void navigator.clipboard
      ?.writeText(prompt)
      .then(() => {
        if (!liveRef.current) return;
        setCopied(true);
        if (resetTimer.current !== null) window.clearTimeout(resetTimer.current);
        resetTimer.current = window.setTimeout(() => {
          if (liveRef.current) setCopied(false);
        }, 1500);
      })
      .catch(() => {
        // Silent — the textarea is selectable as a fallback.
      });
  };
  return (
    <details className="agent-detail-section" open>
      <summary className="agent-detail-section-summary">System prompt</summary>
      <div className="agent-detail-prompt-toolbar">
        <span className="agent-detail-prompt-len">{prompt.length.toLocaleString()} chars</span>
        <button
          type="button"
          className="agent-detail-prompt-copy"
          onClick={onCopy}
          disabled={prompt.length === 0}
        >
          {copied ? 'Copied ✓' : 'Copy'}
        </button>
      </div>
      {prompt.length === 0 ? (
        <p className="agent-detail-prompt-empty">No system prompt.</p>
      ) : (
        <pre className="agent-detail-prompt">{prompt}</pre>
      )}
    </details>
  );
}

// ADR 0043 — the agent's memory attachment map, rendered read-only on the
// detail page alongside Skills + MCP. Scope labels + full access wording.
const MEMORY_DETAIL_SCOPE_ORDER = ['user', 'firm', 'matter'] as const;
const MEMORY_DETAIL_SCOPE_LABEL: Record<(typeof MEMORY_DETAIL_SCOPE_ORDER)[number], string> = {
  user: 'User',
  firm: 'Firm',
  matter: 'Matter',
};
const MEMORY_DETAIL_ACCESS_LABEL: Record<'read_only' | 'read_write', string> = {
  read_only: 'Read-only',
  read_write: 'Read-write',
};

/**
 * Read-only Memory section (ADR 0043): the scopes this agent attaches and at
 * what access mode. Memory is agent-defined (config-repo `memory:` map), so
 * this is informational — not editable here. Always rendered (like Skills +
 * MCP) so "no memory" is explicit.
 */
export function MemorySection({
  memory,
}: {
  readonly memory?: AgentMemoryStatus | undefined;
}): React.ReactElement {
  const scopes =
    memory === undefined
      ? []
      : MEMORY_DETAIL_SCOPE_ORDER.filter((scope) => memory[scope] !== undefined);
  return (
    <details className="agent-detail-section" open>
      <summary className="agent-detail-section-summary">Memory ({scopes.length})</summary>
      {scopes.length === 0 ? (
        <p className="agent-detail-deps-empty">
          No memory attached. Memory is defined by the agent&rsquo;s config-repo memory map.
        </p>
      ) : (
        <dl className="agent-detail-meta">
          {scopes.map((scope) => (
            <div key={scope}>
              <dt>{MEMORY_DETAIL_SCOPE_LABEL[scope]}</dt>
              <dd>{MEMORY_DETAIL_ACCESS_LABEL[memory?.[scope] as 'read_only' | 'read_write']}</dd>
            </div>
          ))}
        </dl>
      )}
    </details>
  );
}

function DependenciesSection({
  agent,
}: {
  agent: ConfigRepoAgentRow;
}): React.ReactElement {
  return (
    <details className="agent-detail-section" open>
      <summary className="agent-detail-section-summary">
        Skills + MCP servers ({agent.skills.length + agent.mcp_servers.length})
      </summary>
      {agent.skills.length === 0 && agent.mcp_servers.length === 0 ? (
        <p className="agent-detail-deps-empty">No skills or MCP servers declared.</p>
      ) : (
        <>
          {agent.skills.length > 0 && (
            <div className="config-repo-agent-skills">
              <span className="config-repo-agent-skills-title">Skills</span>
              <ul className="config-repo-agent-skills-list">
                {agent.skills.map((skill) => (
                  <li
                    key={skill.skill_name}
                    className={`config-repo-agent-skill config-repo-agent-skill-${skill.status}`}
                  >
                    <span className="config-repo-agent-skill-name">{skill.skill_name}</span>
                    <span className="config-repo-agent-skill-id">
                      {skill.ma_skill_id ?? 'None'}
                    </span>
                    <span className="config-repo-agent-skill-status">
                      {formatConfigRepoAgentSkillStatus(skill.status)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {agent.mcp_servers.length > 0 && (
            <div className="config-repo-agent-skills config-repo-agent-mcp-servers">
              <span className="config-repo-agent-skills-title">MCP servers</span>
              <ul className="config-repo-agent-skills-list">
                {agent.mcp_servers.map((server) => (
                  <li
                    key={server.name}
                    className={`config-repo-agent-skill config-repo-agent-mcp-${server.status}`}
                  >
                    <span className="config-repo-agent-skill-name">{server.name}</span>
                    <span className="config-repo-agent-skill-id">{server.url ?? 'None'}</span>
                    <span className="config-repo-agent-skill-status">
                      {formatConfigRepoAgentMcpStatus(server.status)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </details>
  );
}

function RawConfigSection({
  config,
}: {
  config: Record<string, unknown> | null;
}): React.ReactElement {
  return (
    <details className="agent-detail-section agent-detail-raw">
      <summary className="agent-detail-section-summary">Raw config (advanced)</summary>
      <pre className="agent-detail-raw-json">
        {config === null ? 'null' : JSON.stringify(config, null, 2)}
      </pre>
    </details>
  );
}
